import './GstReportLoader.css';

/**
 * GstReportLoader
 * SVG loading overlay for GST Report export.
 * Design: GST tax document with data rows, % badge, govt seal, orbiting dots, sparkles.
 */
const GstReportLoader = ({ open, message = 'Generating GST Report...' }) => {
  if (!open) return null;

  const cx = 55, cy = 55, orbitR = 48;
  const pt = (deg) => {
    const rad = (deg - 90) * (Math.PI / 180);
    return { x: cx + orbitR * Math.cos(rad), y: cy + orbitR * Math.sin(rad) };
  };

  return (
    <div className="gst-backdrop" role="status" aria-live="polite" aria-label={message}>
      <div className="gst-box">
        <svg className="gst-svg" viewBox="0 0 110 110">

          {/* ── Orbiting dots ── */}
          <g className="gst-orbit-wrap">
            {[0, 120, 240].map((deg, i) => {
              const p = pt(deg);
              return <circle key={i} cx={p.x} cy={p.y} r="3.5" fill="#1565c0" opacity="0.7" />;
            })}
          </g>

          {/* ── GST Document (bobs) ── */}
          <g className="gst-doc">
            {/* Document body */}
            <rect x="18" y="12" width="58" height="76" rx="4" fill="#e8f0fe" stroke="#1565c0" strokeWidth="2" />

            {/* Torn bottom */}
            <path d="M18 88 Q22 84 26 88 Q30 92 34 88 Q38 84 42 88 Q46 92 50 88 Q54 84 58 88 Q62 92 66 88 Q70 84 74 88 L76 88 L76 88 L18 88 Z"
              fill="#fff" stroke="none" />

            {/* Header bar */}
            <rect x="18" y="12" width="58" height="18" rx="4" fill="#1565c0" />
            <rect x="18" y="22" width="58" height="8" rx="0" fill="#1565c0" />

            {/* GST label in header */}
            <text x="47" y="24" fontSize="8" fill="#fff" fontWeight="bold" textAnchor="middle">GST</text>
            <rect x="24" y="27" width="16" height="2" rx="1" fill="#bbdefb" opacity="0.7" />

            {/* Column headers */}
            <rect x="22" y="36" width="12" height="2.5" rx="1" fill="#1565c0" opacity="0.5" />
            <rect x="38" y="36" width="16" height="2.5" rx="1" fill="#1565c0" opacity="0.5" />
            <rect x="58" y="36" width="14" height="2.5" rx="1" fill="#1565c0" opacity="0.5" />

            {/* Divider under headers */}
            <line x1="22" y1="41" x2="74" y2="41" stroke="#1565c0" strokeWidth="0.8" opacity="0.4" />

            {/* Data rows — staggered animation */}
            <rect x="22" y="44" width="10" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-1" />
            <rect x="36" y="44" width="18" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-1" />
            <rect x="58" y="44" width="12" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-1" />

            <rect x="22" y="49" width="10" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-2" />
            <rect x="36" y="49" width="14" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-2" />
            <rect x="58" y="49" width="14" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-2" />

            <rect x="22" y="54" width="10" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-3" />
            <rect x="36" y="54" width="16" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-3" />
            <rect x="58" y="54" width="10" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-3" />

            <rect x="22" y="59" width="10" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-4" />
            <rect x="36" y="59" width="18" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-4" />
            <rect x="58" y="59" width="12" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-4" />

            <rect x="22" y="64" width="10" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-5" />
            <rect x="36" y="64" width="12" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-5" />
            <rect x="58" y="64" width="16" height="2" rx="1" fill="#1565c0" opacity="0.3" className="gst-row-5" />

            {/* Total divider + row */}
            <line x1="22" y1="70" x2="74" y2="70" stroke="#1565c0" strokeWidth="1" opacity="0.4" />
            <rect x="22" y="73" width="18" height="2.5" rx="1" fill="#1565c0" opacity="0.35" />
            <rect x="56" y="73" width="16" height="2.5" rx="1" fill="#1565c0" opacity="0.6" />
          </g>

          {/* ── % Tax Badge (spins + pulses) ── */}
          <g className="gst-badge">
            <circle cx="84" cy="28" r="11" fill="#ff6f00" opacity="0.92" />
            <circle cx="84" cy="28" r="8.5" fill="none" stroke="#fff" strokeWidth="0.8" opacity="0.4" />
            <text x="84" y="32" fontSize="10" fill="#fff" fontWeight="bold" textAnchor="middle">%</text>
          </g>

          {/* ── Govt Seal (pulses) ── */}
          <g className="gst-seal">
            <circle cx="16" cy="74" r="9.5" fill="none" stroke="#1565c0" strokeWidth="1.8" strokeDasharray="3,2" />
            <circle cx="16" cy="74" r="6" fill="#e8f0fe" stroke="#1565c0" strokeWidth="1" />
            {/* Checkmark inside seal */}
            <polyline
              points="12,74 15,77 20,70"
              fill="none"
              stroke="#1565c0"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="gst-check"
            />
          </g>

          {/* ── Sparkles ── */}
          <g className="gst-sparkle-1">
            <line x1="90" y1="52" x2="90" y2="58" stroke="#ff6f00" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="87" y1="55" x2="93" y2="55" stroke="#ff6f00" strokeWidth="1.5" strokeLinecap="round" />
          </g>
          <g className="gst-sparkle-2">
            <line x1="14" y1="36" x2="14" y2="41" stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="11.5" y1="38.5" x2="16.5" y2="38.5" stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round" />
          </g>
          <g className="gst-sparkle-3">
            <line x1="90" y1="72" x2="90" y2="76" stroke="#60a5fa" strokeWidth="1.2" strokeLinecap="round" />
            <line x1="88" y1="74" x2="92" y2="74" stroke="#60a5fa" strokeWidth="1.2" strokeLinecap="round" />
          </g>

        </svg>

        <span className="gst-label">{message}</span>
      </div>
    </div>
  );
};

export default GstReportLoader;
